import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Check, Sparkles, ArrowRight, QrCode } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/**
 * QRPricingCards — two-card block matching the exact reference design.
 * Card 1: Custom QR Code · $50 lifetime.
 * Card 2: Dynamic QR Code · $50 activation + $25/mo.
 *
 * Both buttons route through the existing /api/payments/checkout Stripe flow.
 * Kept in a shared component so /pricing and /qr-generator stay in sync.
 */
export function QRPricingCards() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [buying, setBuying] = useState(null);

  const purchase = async (lookup_key) => {
    if (!user) {
      toast.info("Sign in first — it's one tap.");
      const redirectUrl = window.location.origin + "/qr-generator";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    setBuying(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setBuying(null);
    }
  };

  const dynamicActive = !!user?.dynamic_qr_activated && user?.dynamic_qr_subscription_status === "active";
  const dynamicLapsed = !!user?.dynamic_qr_activated && user?.dynamic_qr_subscription_status !== "active";

  return (
    <>
      {/* ── Card 1: Custom QR Code · lifetime ──────────────────── */}
      <section className="mb-8" data-testid="qr-pass-section">
        <div className="flex items-baseline justify-between mb-5">
          <h2 className="font-display text-2xl md:text-3xl text-white">QR Generator</h2>
          <span className="font-mono-hy text-[10px] text-white">One-time · lifetime access</span>
        </div>
        <div
          data-testid="qr-lifetime-pass-card"
          className="relative overflow-hidden p-6 md:p-10 rounded-3xl border border-[#F2C94C]/25"
          style={{
            background:
              "radial-gradient(70% 100% at 0% 100%, rgba(88,28,135,0.45), transparent 60%)," +
              "radial-gradient(70% 100% at 100% 0%, rgba(0,229,255,0.14), transparent 55%)," +
              "linear-gradient(120deg, #120E1F 0%, #0e0a1c 100%)",
          }}
        >
          <div className="grid md:grid-cols-[auto,1fr,auto] items-center gap-6 md:gap-10 relative">
            <div
              className="w-24 h-24 md:w-32 md:h-32 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{
                background: "linear-gradient(135deg, #F2C94C 0%, #E27D60 50%, #7c3aed 100%)",
                boxShadow: "0 0 40px -10px rgba(242,201,76,0.75)",
              }}
            >
              <QrCode className="w-12 h-12 md:w-16 md:h-16 text-[#0A0812]" strokeWidth={2.5} />
            </div>
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F2C94C]/10 border border-[#F2C94C]/30 mb-3">
                <Sparkles className="w-3 h-3 text-[#F2C94C]" />
                <span className="font-mono-hy text-[9px] text-[#F2C94C] tracking-[0.24em]">LIFETIME · UNLIMITED</span>
              </div>
              <h3 className="font-display text-2xl md:text-3xl text-white leading-tight mb-2">Custom QR Code</h3>
              <p className="font-body text-sm md:text-base text-white leading-relaxed max-w-xl">
                Generate your own branded Afro Sci-Fi styled QR code in high resolution, no watermark, yours to keep. Unlock unlimited HD exports across every style — plain, Afro Sci-Fi, custom colors, logo embedded.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {["No watermark", "HD 1024px+", "Unlimited HD renders", "Every style unlocked", "Logo embed"].map((f) => (
                  <span key={f} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.03] border border-white/[0.08] font-mono-hy text-[9px] text-white tracking-[0.14em]">
                    <Check className="w-3 h-3 text-[#00E5FF]" /> {f}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex flex-col items-start md:items-end gap-3 md:min-w-[220px]">
              <div className="font-display text-4xl md:text-5xl text-[#F2C94C] font-bold leading-none">$50</div>
              <div className="font-mono-hy text-[9px] text-white tracking-widest">ONE-TIME · NEVER RENEWS</div>
              {user?.qr_lifetime_pass ? (
                <button
                  onClick={() => navigate("/qr-generator")}
                  data-testid="qr-pass-owned-btn"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl border border-[#00E5FF]/50 bg-[#00E5FF]/[0.08] hover:bg-[#00E5FF]/[0.15] text-[#00E5FF] font-mono-hy text-[10px] tracking-[0.22em] font-bold whitespace-nowrap"
                >
                  <Check className="w-4 h-4" /> UNLOCKED · OPEN QR
                </button>
              ) : (
                <button
                  onClick={() => purchase("qr_lifetime_pass")}
                  disabled={buying === "qr_lifetime_pass"}
                  data-testid="buy-qr-lifetime-pass-btn"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-[#F2C94C] hover:bg-[#F2D06B] text-[#0A0A0A] font-mono-hy text-[10px] tracking-[0.22em] font-bold hover:shadow-[0_0_32px_-4px_rgba(242,201,76,0.85)] disabled:opacity-60 disabled:cursor-not-allowed transition-all whitespace-nowrap"
                >
                  {buying === "qr_lifetime_pass" ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> REDIRECTING…</>
                  ) : (
                    <>BUY NOW <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Card 2: Dynamic QR Code · $50 activation + $25/mo ─────── */}
      <section className="mb-8" data-testid="dynamic-qr-section">
        <div className="flex items-baseline justify-between mb-5">
          <h2 className="font-display text-2xl md:text-3xl text-white">Dynamic QR Code</h2>
          <span className="font-mono-hy text-[10px] text-white">$50 activation · $25/mo</span>
        </div>
        <div
          data-testid="dynamic-qr-card"
          className="relative overflow-hidden p-6 md:p-10 rounded-3xl border border-[#00E5FF]/25"
          style={{
            background:
              "radial-gradient(70% 100% at 100% 100%, rgba(0,229,255,0.20), transparent 60%)," +
              "radial-gradient(70% 100% at 0% 0%, rgba(88,28,135,0.45), transparent 55%)," +
              "linear-gradient(120deg, #0e0a1c 0%, #120E1F 100%)",
          }}
        >
          <div className="grid md:grid-cols-[auto,1fr,auto] items-center gap-6 md:gap-10 relative">
            <div
              className="w-24 h-24 md:w-32 md:h-32 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{
                background: "linear-gradient(135deg, #00E5FF 0%, #7c3aed 50%, #F2C94C 100%)",
                boxShadow: "0 0 40px -10px rgba(0,229,255,0.75)",
              }}
            >
              <QrCode className="w-12 h-12 md:w-16 md:h-16 text-[#0A0812]" strokeWidth={2.5} />
            </div>
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/30 mb-3">
                <Sparkles className="w-3 h-3 text-[#00E5FF]" />
                <span className="font-mono-hy text-[9px] text-[#00E5FF] tracking-[0.24em]">EDITABLE · TRACKABLE</span>
              </div>
              <h3 className="font-display text-2xl md:text-3xl text-white leading-tight mb-2">
                Dynamic QR + Scan Analytics
              </h3>
              <p className="font-body text-sm md:text-base text-white leading-relaxed max-w-xl">
                QR codes you can update anytime — change the destination URL without reprinting. Plus a full scan analytics dashboard tracking every visitor by country and device.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {[
                  "Unlimited dynamic QR codes",
                  "Live destination editing",
                  "Scan analytics dashboard",
                  "Country + device tracking",
                  "Monthly scan reports",
                ].map((f) => (
                  <span key={f} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.03] border border-white/[0.08] font-mono-hy text-[9px] text-white tracking-[0.14em]">
                    <Check className="w-3 h-3 text-[#00E5FF]" /> {f}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex flex-col items-start md:items-end gap-3 md:min-w-[240px]">
              <div className="font-display text-4xl md:text-5xl text-[#00E5FF] font-bold leading-none">$50</div>
              <div className="font-mono-hy text-[9px] text-white tracking-widest">ONE-TIME ACTIVATION</div>
              <div className="font-display text-2xl md:text-3xl text-[#F2C94C] font-bold leading-none mt-1">
                + $25<span className="font-mono-hy text-[10px] text-white/70 font-normal">/mo</span>
              </div>
              <div className="font-mono-hy text-[9px] text-white tracking-widest">RECURRING · CANCEL ANYTIME</div>

              {dynamicActive ? (
                <button
                  onClick={() => navigate("/my-qr-codes")}
                  data-testid="dynamic-qr-owned-btn"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl border border-[#00E5FF]/50 bg-[#00E5FF]/[0.08] hover:bg-[#00E5FF]/[0.15] text-[#00E5FF] font-mono-hy text-[10px] tracking-[0.22em] font-bold whitespace-nowrap"
                >
                  <Check className="w-4 h-4" /> ACTIVE · MANAGE
                </button>
              ) : dynamicLapsed ? (
                <button
                  onClick={() => purchase("dynamic_qr_monthly")}
                  disabled={buying === "dynamic_qr_monthly"}
                  data-testid="dynamic-qr-resubscribe-btn"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-[#F2C94C] hover:bg-[#F2D06B] text-[#0A0A0A] font-mono-hy text-[10px] tracking-[0.22em] font-bold hover:shadow-[0_0_32px_-4px_rgba(242,201,76,0.85)] disabled:opacity-60 disabled:cursor-not-allowed transition-all whitespace-nowrap"
                >
                  {buying === "dynamic_qr_monthly" ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> REDIRECTING…</>
                  ) : (
                    <>RESUBSCRIBE · $25/MO <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>
              ) : (
                <div className="w-full flex flex-col gap-2">
                  <button
                    onClick={() => purchase("dynamic_qr_activation")}
                    disabled={buying === "dynamic_qr_activation"}
                    data-testid="buy-dynamic-qr-activation-btn"
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-[#00E5FF] hover:bg-[#33EDFF] text-[#0A0A0A] font-mono-hy text-[10px] tracking-[0.22em] font-bold hover:shadow-[0_0_32px_-4px_rgba(0,229,255,0.85)] disabled:opacity-60 disabled:cursor-not-allowed transition-all whitespace-nowrap"
                  >
                    {buying === "dynamic_qr_activation" ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> REDIRECTING…</>
                    ) : (
                      <>ACTIVATE · $50 <ArrowRight className="w-4 h-4" /></>
                    )}
                  </button>
                  <p className="font-mono-hy text-[9px] text-white/70 tracking-widest text-center">
                    THEN $25/MO KICKS IN AFTER ACTIVATION
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
